<script>
  import { createEventDispatcher } from 'svelte';
  import Modal from '@components/Modal.svelte';
  import ProgressBar from '@components/ProgressBar.svelte';
  import CreatorSpotlightAjax from '@ajax/creator_spotlight';

  import IntroStep from './creator_spotlight/IntroStep.svelte';
  import MediaUploadStep from './creator_spotlight/MediaUploadStep.svelte';
  import ValuesStep from './creator_spotlight/ValuesStep.svelte';
  import ExpertiseStep from './creator_spotlight/ExpertiseStep.svelte';
  import InterestsStep from './creator_spotlight/InterestsStep.svelte';
  import BrandsStep from './creator_spotlight/BrandsStep.svelte';
  import QuestionsStep from './creator_spotlight/QuestionsStep.svelte';
  import InfoStep from './creator_spotlight/InfoStep.svelte';
  import ListsStep from './creator_spotlight/PinnedListsStep.svelte';

  const dispatch = createEventDispatcher();

  export let showModal = false;
  export let allowClose = true;
  export let mobile = false;
  export let skipIntroStep = false;

  let showAddMediaStep = skipIntroStep ? true : false;
  let showValuesStep = false;
  let showExpertiseStep = false;
  let showInterestsStep = false;
  let showBrandsStep = false;
  let showQuestionsStep = false;
  let showInfoStep = false;
  let showPinnedListsStep = false;
  let progressValue = 5;

  $: showDefaultStep =
    !showAddMediaStep &&
    !showValuesStep &&
    !showExpertiseStep &&
    !showInterestsStep &&
    !showBrandsStep &&
    !showQuestionsStep &&
    !showInfoStep &&
    !showPinnedListsStep;

  let goToDefaultStep = function () {
    showAddMediaStep = false;
    showValuesStep = false;
    showExpertiseStep = false;
    showInterestsStep = false;
    showBrandsStep = false;
    showQuestionsStep = false;
    showInfoStep = false;
    showPinnedListsStep = false;
    progressValue = 0;
  };

  let goToAddMediaStep = function () {
    showAddMediaStep = true;
    showValuesStep = false;
    showExpertiseStep = false;
    showInterestsStep = false;
    showBrandsStep = false;
    showQuestionsStep = false;
    showInfoStep = false;
    showPinnedListsStep = false;
    progressValue = 10;
  };

  let goToValuesStep = function () {
    showAddMediaStep = false;
    showValuesStep = true;
    showExpertiseStep = false;
    showInterestsStep = false;
    showBrandsStep = false;
    showQuestionsStep = false;
    showInfoStep = false;
    showPinnedListsStep = false;
    progressValue = 40;
  };

  let goToExpertiseStep = function () {
    showAddMediaStep = false;
    showValuesStep = false;
    showExpertiseStep = true;
    showInterestsStep = false;
    showBrandsStep = false;
    showQuestionsStep = false;
    showInfoStep = false;
    showPinnedListsStep = false;
    progressValue = 50;
  };

  let goToInterestsStep = function () {
    showAddMediaStep = false;
    showValuesStep = false;
    showExpertiseStep = false;
    showInterestsStep = true;
    showBrandsStep = false;
    showQuestionsStep = false;
    showInfoStep = false;
    showPinnedListsStep = false;
    progressValue = 60;
  };

  let goToBrandsStep = function () {
    showAddMediaStep = false;
    showValuesStep = false;
    showExpertiseStep = false;
    showInterestsStep = false;
    showBrandsStep = true;
    showQuestionsStep = false;
    showInfoStep = false;
    showPinnedListsStep = false;
    progressValue = 70;
  };

  let goToQuestionsStep = function () {
    showAddMediaStep = false;
    showValuesStep = false;
    showExpertiseStep = false;
    showInterestsStep = false;
    showBrandsStep = false;
    showQuestionsStep = true;
    showInfoStep = false;
    showPinnedListsStep = false;
    progressValue = 80;
  };

  let goToInfoStep = function () {
    showAddMediaStep = false;
    showValuesStep = false;
    showExpertiseStep = false;
    showInterestsStep = false;
    showBrandsStep = false;
    showQuestionsStep = false;
    showInfoStep = true;
    showPinnedListsStep = false;
    progressValue = 88;
  };

  let goToPinnedListsStep = function () {
    showAddMediaStep = false;
    showValuesStep = false;
    showExpertiseStep = false;
    showInterestsStep = false;
    showBrandsStep = false;
    showQuestionsStep = false;
    showInfoStep = false;
    showPinnedListsStep = true;
    progressValue = 95;
  };

  let setSectionStatus = async function (data) {
    let res = await CreatorSpotlightAjax.markSpotlightSectionAsComplete(data);
    return res;
  };
</script>

<style style="text/scss" lang="scss">
  @import '../../vendor/design-system.scss';

  * :global(.desktop-modal__container) {
    border-radius: 16px !important;
    min-width: 1100px !important;
    height: calc(100vh - 80px) !important;
    overflow: hidden !important;
    margin-top: 0px !important;
    margin-bottom: 0px !important;
    max-height: 860px !important;
  }

  * :global(.modal__close) {
    position: absolute;
    right: 20px;
    top: 15px;
  }

  .progress-bar-container {
    position: absolute;
    width: 100%;
    top: -16px;
  }

  * :global(.creator-spotlight-modal__container) {
    max-width: 640px !important;
  }
</style>

<div class="svelte-modal svelte-creator-spotlight-modal">
  <Modal
    on:close={() => {
      dispatch('close');
    }}
    show={showModal}
    showCloseButton={showDefaultStep}
    fadeInDuration={0}
    allowClose={allowClose}
    closeOnOverlayClick={false}
    containerClass="creator-spotlight-modal__container">
    <div slot="header" style="position: relative;">
      {#if !showDefaultStep}
        <div class="progress-bar-container"><ProgressBar value={progressValue} /></div>
      {/if}
    </div>
    <div slot="content" style="text-align: center; margin-top: 24px;">
      <div style={showPinnedListsStep ? 'display: block' : 'display: none'}>
        <ListsStep
          mobile={mobile}
          on:close={() => dispatch('close')}
          on:finish={() => dispatch('finish')}
          on:back={() => {
            goToInfoStep();
          }} />
      </div>
      <div style={showInfoStep ? 'display: block' : 'display: none'}>
        <InfoStep
          mobile={mobile}
          on:close={() => dispatch('close')}
          on:back={() => {
            goToQuestionsStep();
          }}
          on:finished={() => {
            goToPinnedListsStep();
          }} />
      </div>
      <div style={showQuestionsStep ? 'display: block' : 'display: none'}>
        <QuestionsStep
          mobile={mobile}
          on:close={() => dispatch('close')}
          on:back={() => {
            goToBrandsStep();
          }}
          on:finished={() => {
            setSectionStatus({ questions_added: true });
            goToInfoStep();
          }} />
      </div>
      <div style={showBrandsStep ? 'display: block' : 'display: none'}>
        <BrandsStep
          mobile={mobile}
          on:close={() => dispatch('close')}
          on:back={() => {
            goToInterestsStep();
          }}
          on:finished={() => {
            setSectionStatus({ brands_added: true });
            goToQuestionsStep();
          }} />
      </div>
      <div style={showInterestsStep ? 'display: block' : 'display: none'}>
        <InterestsStep
          mobile={mobile}
          on:close={() => dispatch('close')}
          on:back={() => {
            goToExpertiseStep();
          }}
          on:finished={() => {
            setSectionStatus({ interests_added: true });
            goToBrandsStep();
          }} />
      </div>
      <div style={showExpertiseStep ? 'display: block' : 'display: none'}>
        <ExpertiseStep
          mobile={mobile}
          on:close={() => dispatch('close')}
          on:back={() => {
            goToValuesStep();
          }}
          on:finished={() => {
            setSectionStatus({ expertises_added: true });
            goToInterestsStep();
          }} />
      </div>
      <div style={showValuesStep ? 'display: block' : 'display: none'}>
        <ValuesStep
          mobile={mobile}
          on:close={() => dispatch('close')}
          on:back={() => {
            goToAddMediaStep();
          }}
          on:finished={() => {
            setSectionStatus({ values_added: true });
            goToExpertiseStep();
          }} />
      </div>
      <div style={showAddMediaStep ? 'display: block' : 'display: none'}>
        <MediaUploadStep
          mobile={mobile}
          hideBackButton={skipIntroStep}
          on:close={() => dispatch('close')}
          on:back={() => {
            goToDefaultStep();
          }}
          on:finished={() => {
            setSectionStatus({ gallery_items_added: true });
            goToValuesStep();
          }} />
      </div>
      <div style={showDefaultStep ? 'display: block' : 'display: none'}>
        <IntroStep
          mobile={mobile}
          on:finished={() => {
            goToAddMediaStep();
          }} />
      </div>
    </div>
  </Modal>
</div>
